import React, { useContext, useEffect, useState } from 'react';
import { ErrorType, GatekeeperRejectionPayload } from '../../../utils/errors';
import UniquenessError from './UniquenessError';
import { FlowConfigContext } from '../../../providers/FlowConfigContext';
import { Resolver } from '../../../config';

interface Props {
  onConfirm: () => void;
  errorType: ErrorType;
}

/**
 * Represents an error component for uniqueness error by encapsulating the logic for displaying the correct error
 * and uniqueness pass revocation status if applicable.
 * @param onConfirm - The callback function to handle the confirmation action.
 * @param errorType - The type of error.
 * @returns The JSX element representing the uniqueness rejected error component.
 */
const UniquenessRejectedError: React.FC<Props> = ({ onConfirm, errorType }): JSX.Element => {
  const { initialized, chainName, networkAddress, chainNetwork, getOptionalFlowExecutionConfig } =
    useContext(FlowConfigContext);
  const [matches, setMatches] = useState<string[]>([]);
  const [revocationTimestamp, setRevocationTimestamp] = useState<number>();
  const [revocationPortalUrl, setRevocationPortalUrl] = useState<string>('');

  useEffect(() => {
    if (!initialized) {
      return;
    }

    const baseUrl = Resolver.getServiceConfig().uniqueness.revocationPortalUrl;
    setRevocationPortalUrl(baseUrl);
    const providedPayload = JSON.parse(getOptionalFlowExecutionConfig('payload') || '{}') as GatekeeperRejectionPayload;
    if (errorType === ErrorType.NotUnique) {
      const providedMatches = providedPayload.matches || [];
      setMatches(providedMatches);
    } else if (errorType === ErrorType.RevocationTooRecent) {
      if (providedPayload && providedPayload.revocationTimestamp) {
        setRevocationTimestamp(providedPayload.revocationTimestamp);
      }
    }
  }, [initialized, chainNetwork, chainName, networkAddress, errorType]);

  return (
    <UniquenessError
      revocationPortalUrl={revocationPortalUrl}
      matches={matches}
      onConfirm={onConfirm}
      revocationTimestamp={revocationTimestamp}
      errorType={errorType}
    />
  );
};

export default UniquenessRejectedError;
